import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faPause, faPen, faPlay, faTrash } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

import {
  notificationApi,
  NotificationApplication,
  NotificationChannel,
  Subscription,
  Topic,
  describeTagFilter,
  FieldValuesSummary,
  SubscriptionWizardModal,
} from '@/core/notifications/export';
import { AppModal } from '@/shared/components/export';
import '@/shared/theme/notifications-settings.scss';

const ACTION_BUTTON_STYLE = { padding: '3px 8px', lineHeight: 1 };

function StatusPill({ paused }: { paused: boolean }) {
  return (
    <span
      style={{
        fontSize: 12,
        borderRadius: 3,
        padding: '3px 8px',
        fontWeight: 600,
        background: paused
          ? 'rgba(var(--bs-warning-rgb), 0.15)'
          : 'rgba(var(--bs-success-rgb), 0.15)',
        color: paused ? 'var(--bs-warning)' : 'var(--bs-success)',
        flexShrink: 0,
        whiteSpace: 'nowrap',
      }}
    >
      {paused ? 'Paused' : 'Active'}
    </span>
  );
}

export default function PipelineSubscriptionsModal({
  subscriptions,
  topics,
  applications,
  channels,
  onClose,
  onChanged,
}: {
  subscriptions: Subscription[];
  topics: Topic[];
  applications: NotificationApplication[];
  channels: NotificationChannel[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState<Subscription | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Subscription | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const togglePause = async (subscription: Subscription) => {
    setBusyId(subscription.id);
    try {
      await notificationApi.updateSubscription(subscription.id, { paused: !subscription.paused });
      toast.success(subscription.paused ? 'Alarm resumed' : 'Alarm paused');
      onChanged();
    } catch {
      toast.error('Could not update the alarm');
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setBusyId(pendingDelete.id);
    try {
      await notificationApi.deleteSubscription(pendingDelete.id);
      toast.success('Alarm deleted');
      onChanged();
    } catch {
      toast.error('Could not delete the alarm');
    } finally {
      setBusyId(null);
      setPendingDelete(null);
    }
  };

  return (
    <>
      <AppModal
        show
        onClose={onClose}
        size={'lg'}
        className={'notifications-settings'}
        icon={faBell}
        title={'Pipeline Alarms'}
        cancelLabel={'Close'}
      >
        {subscriptions.length === 0 ? (
          <div className={'py-3 text-center mutedText'} style={{ fontSize: 14 }}>
            No pipeline alarms yet — create one from the Alarms menu.
          </div>
        ) : (
          subscriptions.map(subscription => {
            const topic = topics.find(t => t.key === subscription.topic);
            const filters = topic ? describeTagFilter(topic, subscription.tagFilter) : [];
            const channelNames = subscription.defaultChannels
              .map(key => channels.find(channel => channel.key === key)?.displayName ?? key)
              .join(', ');
            const busy = busyId === subscription.id;

            return (
              <div
                key={subscription.id}
                className={'d-flex align-items-start gap-2 py-2 border-bottom'}
              >
                <div className={'flex-grow-1'} style={{ minWidth: 0 }}>
                  <div className={'d-flex align-items-center gap-2'} style={{ minWidth: 0 }}>
                    <StatusPill paused={subscription.paused} />
                    <span
                      className={'text-truncate'}
                      style={{ fontWeight: 600 }}
                      title={subscription.name}
                    >
                      {subscription.name}
                    </span>
                  </div>
                  <div className={'mutedText mt-1'} style={{ fontSize: 12 }}>
                    {filters.length === 0 ? (
                      'Every pipeline notice'
                    ) : (
                      <FieldValuesSummary entries={filters} />
                    )}
                  </div>
                  {channelNames && (
                    <div className={'mutedText'} style={{ fontSize: 12 }}>
                      Delivered to: {channelNames}
                    </div>
                  )}
                </div>

                <div className={'d-flex align-items-center gap-1 flex-shrink-0'}>
                  <button
                    type={'button'}
                    className={'btn btn-sm btn-outline-secondary'}
                    style={ACTION_BUTTON_STYLE}
                    disabled={busy}
                    onClick={() => togglePause(subscription)}
                    aria-label={subscription.paused ? 'Resume' : 'Pause'}
                    title={subscription.paused ? 'Resume' : 'Pause'}
                  >
                    <FontAwesomeIcon icon={subscription.paused ? faPlay : faPause} />
                  </button>
                  <button
                    type={'button'}
                    className={'btn btn-sm btn-outline-secondary'}
                    style={ACTION_BUTTON_STYLE}
                    disabled={busy}
                    onClick={() => setEditing(subscription)}
                    aria-label={'Edit'}
                    title={'Edit'}
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                  <button
                    type={'button'}
                    className={'btn btn-sm btn-outline-secondary'}
                    style={ACTION_BUTTON_STYLE}
                    disabled={busy}
                    onClick={() => setPendingDelete(subscription)}
                    aria-label={'Delete'}
                    title={'Delete'}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </AppModal>

      <AppModal
        show={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        icon={faTrash}
        title={'Confirm Deletion'}
        primaryLabel={'Confirm Delete'}
        onPrimary={confirmDelete}
        primaryDanger
      >
        Are you sure you want to delete the alarm <strong>{pendingDelete?.name}</strong>? This
        action cannot be undone.
      </AppModal>

      {editing && (
        <SubscriptionWizardModal
          existingSubscription={editing}
          topics={topics}
          applications={applications}
          channels={channels}
          onCancel={() => setEditing(null)}
          onDone={() => {
            onChanged();
            setEditing(null);
          }}
        />
      )}
    </>
  );
}
